import type { ScopedMutator } from "swr";

/**
 * One project's cached reads, in every shape the app fetches them.
 *
 * A close is a project, and its tasks are fetched three ways: the Projects
 * task list (`/tasks?{json}`), the close's all-tasks fetch
 * (`tasks?project=N&...`), and the close's own endpoints (`close/...`). An
 * edit made on one screen left the others stale until a refresh. Every write
 * to a project or one of its tasks calls `invalidateProject`, which
 * revalidates all of them by predicate, so no caller has to know the other
 * screens' keys.
 */

const TASK_LIST = "/tasks?";
const CLOSE_TASKS = "tasks?";
/** A task's linked work (`useTaskLinks`). The key does not name its project. */
const TASK_LINKS = /^tasks\/\d+\/links$/;
/**
 * "n of m requirements met" for the tasks a list shows (`useWorkLinkCounts`).
 * The key lists task ids, not a project, so every write revalidates it.
 */
const WORK_LINK_COUNTS = "work-links/counts";

function taskListNamesProject(key: string, projectId: number): boolean {
	try {
		const params = JSON.parse(key.slice(TASK_LIST.length)) as {
			project?: unknown;
		};
		// No project filter (All tasks, Kanban, Calendar): the task is in it.
		if (params.project === undefined || params.project === null) return true;
		return Number(params.project) === projectId;
	} catch {
		return false;
	}
}

/**
 * Whether an SWR key reads `projectId`'s data. With no id, whether it reads
 * any project's, task's or close's: for a write whose project is not known.
 */
export function isProjectKey(key: unknown, projectId?: number): boolean {
	if (typeof key !== "string") return false;
	// Board, lookup, detail and My Week: a close key does not name its project.
	if (key.startsWith("close/")) return true;
	// The Projects list rows carry the status and the counts.
	if (key.startsWith("/projects?") || key.startsWith("projects?")) return true;
	// A link's state follows the work, and a task write can pause or resume it.
	if (TASK_LINKS.test(key)) return true;
	// And so do the lists' requirement counts.
	if (key.startsWith(WORK_LINK_COUNTS)) return true;
	if (projectId === undefined) {
		return (
			/^projects\/\d+$/.test(key) ||
			(key.startsWith(TASK_LIST) &&
				key.slice(TASK_LIST.length).startsWith("{")) ||
			key.startsWith(`${CLOSE_TASKS}project=`)
		);
	}
	if (key === `projects/${projectId}`) return true;
	if (key.startsWith(`${CLOSE_TASKS}project=${projectId}&`)) return true;
	if (key.startsWith(TASK_LIST)) return taskListNamesProject(key, projectId);
	return false;
}

/**
 * Refetch everything that shows `projectId` (or every project, with no id).
 * A revalidation, not a cache write: what is on screen stays until the fresh
 * data arrives.
 *
 * `except` names one key to leave alone: a write that has just stored the
 * server's answer under that key has nothing to gain from reading it again.
 */
export function invalidateProject(
	mutate: ScopedMutator,
	projectId?: number,
	options: { except?: string } = {},
): Promise<unknown> {
	// One argument only: in SWR 2.5.1 any data argument, even `undefined`,
	// takes the write path and empties every matching key before the refetch.
	return mutate(
		(key) => key !== options.except && isProjectKey(key, projectId),
	);
}
